import { filing } from './toolkit';

/** IRS Form SS-4 (Rev. December 2019): Application for Employer Identification Number, completed for a decedent's estate. */
export default filing('file-1787166823127-1e0e95d8-971b-454b-ad9e-052d2e3c12fb.pdf', 'Estate EIN application (SS-4)', f => {
  const sf = (n: number) => `topmostSubform[0].Page1[0].f1_${n}[0]`;
  const cb = (n: number, i: number) => `topmostSubform[0].Page1[0].c1_${n}[${i}]`;
  const ESTATE = 'This application is for a decedent\'s estate.';

  f.notApplicable('topmostSubform[0].Page1[0].PageOneHeader[0].f1_1[0]', 'Header: EIN (assigned by the IRS)',
    'Entered by the IRS on the returned copy when it assigns the EIN. The applicant leaves it blank.');

  // Lines 1 to 6: the estate.
  f.template(sf(2), 'Estate of {{decedent.fullName}}', '1. Legal name of entity for whom the EIN is being requested',
    { location: 'IRS instructions: for an estate, enter the name of the estate (Estate of the decedent).' });
  f.input(sf(3), 'tradeName', '2. Trade name of business (if different from name on line 1)', 'Lines 2 to 5: names and addresses', 'text',
    'Usually blank for an estate. Complete only if the estate operates a business under another name.');
  f.shared(sf(4), 'executor.fullName', '3. Executor, administrator, trustee, "care of" name');
  f.template(sf(5), '{{estate.addressLine1}} {{estate.addressLine2}}', '4a. Mailing address (room, apt., suite no. and street, or P.O. box)');
  f.input(sf(6), 'businessStreet', "5a. Street address (if different) (Don't enter a P.O. box)", 'Lines 2 to 5: names and addresses', 'text',
    'Leave blank unless the estate has a business location different from the mailing address.');
  f.template(sf(7), '{{estate.city}}, {{estate.state}} {{estate.zipCode}}', '4b. City, state, and ZIP code (mailing)');
  f.input(sf(8), 'businessCityStateZip', '5b. City, state, and ZIP code (street address, if different)', 'Lines 2 to 5: names and addresses', 'text',
    'Leave blank unless you completed 5a.');
  f.template(sf(9), '{{estate.county}}, {{estate.state}}', '6. County and state where principal business is located');

  // Line 7: responsible party of an estate is its executor or administrator.
  f.shared(sf(10), 'executor.fullName', '7a. Name of responsible party (the executor or administrator)');
  f.shared(sf(11), 'executor.ssn', '7b. Responsible party: SSN, ITIN, or EIN');

  // Line 8: LLC questions do not apply to an estate.
  f.fixed(cb(1, 0), 'unchecked', '8a. Is this application for an LLC? Yes', ESTATE);
  f.fixed(cb(1, 1), 'checked', '8a. Is this application for an LLC? No', ESTATE);
  f.notApplicable(sf(12), '8b. If 8a is "Yes," number of LLC members', ESTATE);
  f.notApplicable(cb(2, 0), '8c. If 8a is "Yes," was the LLC organized in the United States? Yes', ESTATE);
  f.notApplicable(cb(2, 1), '8c. If 8a is "Yes," was the LLC organized in the United States? No', ESTATE);

  // Line 9a: type of entity is always Estate.
  const other = (i: number, label: string) => f.fixed(cb(3, i), 'unchecked', `9a. Type of entity: ${label}`, ESTATE);
  other(0, 'Sole proprietor (SSN)');
  f.notApplicable(sf(13), '9a. Sole proprietor: SSN', ESTATE);
  f.fixed(cb(3, 1), 'checked', '9a. Type of entity: Estate (SSN of decedent)', ESTATE);
  f.shared(sf(14), 'decedent.ssn', '9a. Estate: SSN of decedent');
  other(2, 'Partnership');
  other(3, 'Plan administrator (TIN)');
  f.notApplicable(sf(15), '9a. Plan administrator: TIN', ESTATE);
  other(4, 'Corporation (enter form number to be filed)');
  f.notApplicable(sf(16), '9a. Corporation: form number to be filed', ESTATE);
  other(5, 'Trust (TIN of grantor)');
  f.notApplicable(sf(17), '9a. Trust: TIN of grantor', ESTATE);
  other(6, 'Personal service corporation');
  other(7, 'Military/National Guard');
  other(8, 'State/local government');
  other(9, 'Church or church-controlled organization');
  other(10, "Farmers' cooperative");
  other(11, 'Federal government');
  other(12, 'Other nonprofit organization (specify)');
  f.notApplicable(sf(18), '9a. Other nonprofit organization: specify', ESTATE);
  other(13, 'REMIC');
  other(14, 'Indian tribal governments/enterprises');
  other(15, 'Other (specify)');
  f.notApplicable(sf(19), '9a. Other entity type: specify', ESTATE);
  f.notApplicable(sf(20), '9a. Group Exemption Number (GEN) if any', 'Applies only to organizations covered by a group exemption, never to an estate.');
  f.notApplicable(sf(21), '9b. If a corporation, state where incorporated', ESTATE);
  f.notApplicable(sf(22), '9b. If a corporation, foreign country where incorporated', ESTATE);

  // Line 10: reason for applying.
  const reason = '10. Reason for applying';
  f.choice('einApplicationReason', '10. Reason for applying (check only one box)', reason, [
    { label: 'Banking purpose (specify purpose)', value: 'Banking purpose', fields: [cb(4, 0)] },
    { label: 'Started new business (specify type)', value: 'Started new business', fields: [cb(4, 1)] },
    { label: 'Changed type of organization (specify new type)', value: 'Changed type of organization', fields: [cb(4, 2)] },
    { label: 'Purchased going business', value: 'Purchased going business', fields: [cb(4, 3)] },
    { label: 'Hired employees (check the box and see line 13)', value: 'Hired employees', fields: [cb(4, 4)] },
    { label: 'Created a trust (specify type)', value: 'Created a trust', fields: [cb(4, 5)] },
    { label: 'Compliance with IRS withholding regulations', value: 'Compliance with withholding rules', fields: [cb(4, 6)] },
    { label: 'Created a pension plan (specify type)', value: 'Created a pension plan', fields: [cb(4, 7)] },
    { label: 'Other (specify)', value: 'Other', fields: [cb(4, 8)] },
  ], 'checkbox', 'Estates commonly check Other and specify "Estate administration", or Banking purpose.');
  f.input(sf(24), 'bankingPurpose', '10. Banking purpose: specify purpose', reason);
  f.input(sf(25), 'newBusinessType', '10. Started new business: specify type', reason);
  f.notApplicable(sf(26), '10. Started new business: second line', 'Continuation line. The business type prints on the first line.');
  f.input(sf(27), 'changedOrganizationType', '10. Changed type of organization: specify new type', reason);
  f.input(sf(28), 'trustType', '10. Created a trust: specify type', reason);
  f.input(sf(29), 'pensionPlanType', '10. Created a pension plan: specify type', reason);
  f.input(sf(30), 'otherApplicationReason', '10. Other: specify', reason, 'text', 'For example: Estate administration.');

  // Lines 11 to 15.
  f.shared(sf(31), 'decedent.dateOfDeath', '11. Date business started or acquired (for an estate: the date of death)');
  const acct = '12 to 15. Accounting year and employees';
  f.input(sf(32), 'accountingClosingMonth', '12. Closing month of accounting year', acct, 'text',
    'Month name, for example December. An estate may choose a fiscal year ending on the last day of any month.');
  f.input(sf(33), 'agriculturalEmployees', '13. Highest number of employees expected in the next 12 months: Agricultural', acct, 'number', 'Enter 0 if none.');
  f.input(sf(34), 'householdEmployees', '13. Highest number of employees expected in the next 12 months: Household', acct, 'number', 'Enter 0 if none.');
  f.input(sf(35), 'otherEmployees', '13. Highest number of employees expected in the next 12 months: Other', acct, 'number', 'Enter 0 if none.');
  f.choice('annualEmploymentReturn', '14. Expect employment tax liability of $1,000 or less and want to file Form 944 annually instead of Forms 941 quarterly?', acct, [
    { label: 'Yes (check the box)', value: 'Yes', fields: [cb(5, 0)] },
    { label: 'No (file Form 941 every quarter)', value: 'No', fields: [] },
  ], 'checkbox', 'Skip if no employees are expected.');
  f.input(sf(36), 'firstWagesDate', '15. First date wages or annuities were paid (or income first paid to a nonresident alien)', acct, 'date',
    'Leave blank if the estate will not pay wages or annuities.');

  // Lines 16 and 17: principal activity.
  const activity = '16 and 17. Principal activity';
  f.choice('principalActivity', '16. Check one box that best describes the principal activity of your business', activity, [
    { label: 'Health care & social assistance', value: 'Health care and social assistance', fields: [cb(6, 0)] },
    { label: 'Wholesale-agent/broker', value: 'Wholesale agent or broker', fields: [cb(6, 1)] },
    { label: 'Construction', value: 'Construction', fields: [cb(6, 2)] },
    { label: 'Rental & leasing', value: 'Rental and leasing', fields: [cb(6, 3)] },
    { label: 'Transportation & warehousing', value: 'Transportation and warehousing', fields: [cb(6, 4)] },
    { label: 'Accommodation & food service', value: 'Accommodation and food service', fields: [cb(6, 5)] },
    { label: 'Wholesale-other', value: 'Wholesale other', fields: [cb(6, 6)] },
    { label: 'Retail', value: 'Retail', fields: [cb(6, 7)] },
    { label: 'Real estate', value: 'Real estate', fields: [cb(6, 8)] },
    { label: 'Manufacturing', value: 'Manufacturing', fields: [cb(6, 9)] },
    { label: 'Finance & insurance', value: 'Finance and insurance', fields: [cb(6, 10)] },
    { label: 'Other (specify)', value: 'Other', fields: [cb(6, 11)] },
  ], 'checkbox', 'Estates that only hold and distribute assets commonly check Other and specify "Estate administration".');
  f.input(sf(37), 'otherPrincipalActivity', '16. Other principal activity: specify', activity);
  f.input(sf(38), 'activityDescription', '17. Principal line of merchandise sold, specific construction work done, products produced, or services provided', activity, 'textarea');

  // Line 18.
  const prior = '18. Prior EIN';
  f.choice('previousEinReceived', '18. Has the applicant entity shown on line 1 ever applied for and received an EIN?', prior, [
    { label: 'Yes', fields: [cb(7, 0)] },
    { label: 'No', fields: [cb(7, 1)] },
  ], 'checkbox');
  f.shared(sf(39), 'estate.ein', '18. If "Yes," previous EIN',
    { location: 'Estate: EIN. Clear it or answer No on line 18 if the estate has no earlier EIN.' });

  // Third party designee (optional).
  f.person('einDesignee', 'Third party designee (optional): authorized to receive the EIN and answer questions about this form', 'Third party designee',
    'Leave empty unless you want to authorize someone (often the attorney). Their name, address, phone and fax come from their participant record.');
  f.personField(sf(40), 'einDesignee', 'fullName', "Third party designee: designee's name");
  f.personField(sf(41), 'einDesignee', 'phone', "Third party designee: designee's telephone number");
  f.personField(sf(42), 'einDesignee', 'fullAddress', 'Third party designee: address and ZIP code');
  f.personField(sf(43), 'einDesignee', 'fax', "Third party designee: designee's fax number");

  // Signature block (the signature and date are printed text, not widgets, and are completed by hand).
  f.question('signerTitle', 'Title of the person signing (for example Executor or Administrator)', 'Signature', 'text',
    'Printed after the executor\'s name on "Name and title".');
  f.filingTemplate(sf(44), '{{executor.fullName}}, {{probateFiling.signerTitle}}', 'Name and title (type or print clearly)');
  f.shared(sf(45), 'executor.phone', "Applicant's telephone number");
  f.shared(sf(46), 'executor.fax', "Applicant's fax number");
});
